import { forwardRef, useRef, useState } from "react";
import $ from "jquery";
import dom from "zeta-dom/dom";
import { bind } from "zeta-dom/domUtil";
import { watchElements } from "zeta-dom/observe";
import { combineFn, matchWord } from "zeta-dom/util";
import { classNames, toRefCallback, TextFieldProps as BaseTextFieldProps, useFormField } from "zeta-dom-react";
import { Mixin, useFocusStateMixin } from "brew-js-react";
import { Icon, IconNames } from "src/components/common";
import { position } from "src/util/cssUtil";
import { ValidatorProps, validators } from ".";

export interface TextFieldProps extends BaseTextFieldProps, ValidatorProps {
    label?: string;
    icon?: IconNames;
    helpText?: string;
    helpTextIcon?: IconNames;
    required?: boolean;
    pattern?: string;
    multiline?: boolean;
    toggle?: IconNames;
    onToggle?: () => void;
}

const $helpTextDiv = $('<div class="eac-text-field-helptext"></div>');
const $helpTextDivArrow = $('<div class="eac-text-field-helptext-arrow"></div>');

let windowFocused = false;
let unbind = bind(window, 'focus mousedown keydown', _ => {
    windowFocused = true;
    unbind();
});

watchElements(dom.root, 'input', addedNodes => {
    for (let input of addedNodes as HTMLInputElement[]) {
        // prevent auto-complete conflicting empty state
        // as auto-filled value are not available until user has interaction with the page
        if (!windowFocused && !matchWord(input.type, 'radio checkbox image button') && !input.value) {
            const unbind1 = bind(input, 'change focus', () => {
                unbind1();
                input.style.opacity = '';
            });
            input.style.opacity = '0';
        }
    }
});

function setHelpText(target: Element, visible: boolean, helpText: string) {
    if (visible) {
        $helpTextDivArrow.appendTo(document.body);
        $helpTextDiv.text(helpText);
        position($helpTextDiv[0], target, 'top center', undefined, $helpTextDivArrow.outerHeight());
        position($helpTextDivArrow[0], target, 'top center');
    } else {
        $helpTextDiv.detach();
        $helpTextDivArrow.detach();
    }
}

function renderTextFieldFeature(value: string, props: React.PropsWithChildren<TextFieldProps>) {
    return (
        <>
            {props.icon &&
                <Icon className="eac-text-field-icon" icon={props.icon} />}
            {props.label &&
                <div className="eac-text-field-label">{props.label}</div>}
            {props.toggle &&
                <Icon className="eac-text-field-toggle" icon={props.toggle} onClick={props.onToggle} />}
            {props.multiline && props.maxLength &&
                <div className="eac-text-field-word-count">{value.length} / {props.maxLength}</div>}
            {props.helpText &&
                <Icon className="eac-text-field-toggle" icon={props.helpTextIcon || 'inquiry'} onMouseOver={(e) => setHelpText(e.target as Element, true, props.helpText!)} onMouseOut={(e) => setHelpText(e.target as Element, false, props.helpText!)} />}
        </>
    );
}

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>((props, ref) => {
    props = {
        ...props,
        onValidate: validators.combine(
            props.required && validators.required(),
            props.pattern && validators.pattern(props.pattern),
            props.onValidate
        )
    };
    const mixin = useFocusStateMixin();
    const inputRef = useRef<HTMLInputElement>(null);
    const isPassword = props.type === 'password';
    const [unmask, setUnmask] = useState(false);
    const { value, error, setValue, elementRef, inputProps } = useFormField('text', props, '');

    if (isPassword) {
        props.toggle = unmask ? 'hide' : 'show';
        props.onToggle = () => setUnmask(v => !v);
    }
    if (value && inputRef.current?.style.opacity === '0') {
        inputRef.current.style.opacity = '';
    }
    ref = combineFn(elementRef, toRefCallback(ref));

    const inputType = isPassword && unmask ? 'text' : props.type || 'text';
    return (
        <label {...Mixin.use(ref, mixin, classNames('eac-field eac-text-field', { disabled: props.disabled, empty: !value, 'is-multiline': props.multiline, 'has-icon': !!props.icon, 'has-error': !!error }))}>
            <div className="eac-text-field-outline">
                {renderTextFieldFeature(value, props)}
                <Icon className="eac-text-field-clear" icon="close" onClick={() => setValue('')} />
                {props.multiline ?
                    <textarea name={props.name} value={value} {...inputProps}
                        onChange={e => setValue(e.target.value)} /> :
                    <input ref={inputRef} name={props.name} value={value} {...inputProps} type={inputType}
                        onChange={e => setValue(e.target.value)} />}
            </div>
            {error && props.showErrorMessage !== false &&
                <div className="eac-field-error">{error}</div>}
        </label>
    );
});
